import { getConfig } from '@superbet-group/web.lib.config';
import { Site } from '@superbet-group/web.types';
import {
    MultipleDomainDestination,
    NavigationDestination,
    NavigationKind,
} from '@app/router/types';
import {
    getMeticulousSite,
    isMeticulousContext,
} from './meticulous/meticulousUtils';

type Domain = {
    subdomain?: string; // e.g. 'local.dev'
    origin: string; // e.g. 'superbet.ro'
    secondLevel: string; // e.g. 'superbet'
    topLevel: string; // e.g. 'ro'
};

const superbetWebSubdomains = [
    'local.prod',
    'local.dev',
    'local.qa',
    'local.stage',
    'local',
    'dev',
    'qa',
    'stage',
    '',
];

const siteDomainMap: Record<Site, string> = {
    [Site.NapoleonGames]: 'napoleongames.be',
    [Site.NapoleonSports]: 'napoleonsports.be',
    [Site.NapoleonCasino]: 'napoleoncasino.be',
    [Site.NapoleonDice]: 'napoleondice.be',
    [Site.SuperbetRo]: 'superbet.ro',
    [Site.SuperbetPl]: 'superbet.pl',
    [Site.SuperbetRs]: 'superbet.rs',
    [Site.SuperbetCom]: 'superbet.com',
    [Site.SuperbetBr]: 'superbet.bet.br',
    [Site.SuperbetBg]: 'superbet.bg',
    [Site.SuperSocialBr]: 'supersocial.com.br',
    [Site.NapoleonGamesRo]: 'napoleongames.ro',
    [Site.SuperbetBetlerPl]: 'be.superbet.pl',
};

export function parseDomain(hostname: string): Domain {
    const knownTwoLevelTLDs = ['com.br', 'bet.br'];

    const parts = hostname.split('.');
    let topLevel = '';
    let secondLevel = '';
    let subdomain = '';

    const matchedTwoLevel = knownTwoLevelTLDs.find((tld) => hostname.endsWith(tld));

    if (matchedTwoLevel) {
        topLevel = matchedTwoLevel;
        const tldParts = matchedTwoLevel.split('.');
        parts.splice(parts.length - tldParts.length, tldParts.length);

        secondLevel = parts.pop() || '';
        subdomain = parts.join('.');
    } else {
        topLevel = parts.pop() || '';
        secondLevel = parts.pop() || '';
        subdomain = parts.join('.');
    }

    return {
        origin: secondLevel ? `${secondLevel}.${topLevel}` : topLevel,
        topLevel,
        secondLevel,
        subdomain,
    };
}

export function isSuperbetWebDomain(hostname: string) {
    const urlDomain = parseDomain(hostname);

    return urlDomain.secondLevel === 'superbet' && superbetWebSubdomains.includes(urlDomain.subdomain || '');
}

export function areTopAndSecondLevelDomainsSame(currentHostname: string, targetHostname: string): boolean {
    const urlDomain = parseDomain(currentHostname);
    const locationDomain = parseDomain(targetHostname);

    return urlDomain.secondLevel === locationDomain.secondLevel
        && urlDomain.topLevel === locationDomain.topLevel;
}

export function isNeighbouringSubDomain(locationHostname: string, urlHostname: string): boolean {
    const urlDomain = parseDomain(urlHostname);
    const locationDomain = parseDomain(locationHostname);

    return urlDomain.topLevel === locationDomain.topLevel
        && urlDomain.secondLevel === locationDomain.secondLevel
        && urlDomain.subdomain !== locationDomain.subdomain;
}

export function isSuperbetDeepLink(deeplinkHref: string): boolean {
    const deeplinkRoot = deeplinkHref.split(':').shift() || '';

    return !!getConfig().countrySpecific.inbox?.availableDeepLinks.includes(deeplinkRoot);
}

export function getCurrentSite(hostname: string): Site {
    // Temporary workaround for meticulous runs during the PoC phase.
    if (hostname.includes('meticulous.ai')) {
        return getMeticulousSite();
    }

    if (hostname.includes('supersocial.com.br')) {
        return Site.SuperSocialBr;
    }

    if (hostname.endsWith('superbet.bet') || hostname.includes('superbet.com.br')) {
        return Site.SuperbetCom;
    }
    const { origin } = parseDomain(hostname);
    const site = (Object.keys(siteDomainMap) as (keyof typeof siteDomainMap)[]).find((key) => {
        return siteDomainMap[key].endsWith(origin);
    });

    if (!site && isMeticulousContext()) {
        return getMeticulousSite();
    }

    if (site === Site.SuperbetPl && hostname.includes('-be')) {
        return Site.SuperbetBetlerPl;
    }

    return site!;
}

export function isExternalDestination(
    hostname: string,
    navigationDestination: NavigationDestination,
): navigationDestination is MultipleDomainDestination {
    const site = getCurrentSite(hostname);
    return navigationDestination.kind === NavigationKind.MultipleDomain && navigationDestination.site !== site;
}

export function getAdjacentUrl(hostname: string, site: Site, path?: string): string {
    const port = getConfig().environment.isDevelopment ? `:${window.location.port}` : '';
    const adjacentHostname = getAdjacentHostname(hostname, site);
    return `https://${adjacentHostname}${port}${path || ''}`;
}

export function getAdjacentHostname(currentHostname: string, adjacentSite: Site): string {
    const { origin } = parseDomain(currentHostname);
    let adjecentHostname = siteDomainMap[adjacentSite];
    if (window.location.hostname.includes('superbet.bet')) {
        adjecentHostname = 'superbet.bet';
    } else if (window.location.hostname.includes('superbet.com.br')) {
        adjecentHostname = 'superbet.com.br';
    }
    return currentHostname.replace(origin, adjecentHostname);
}

export function getMultiDomainField(field: Record<Site, string>) {
    const site = currentSite;
    return field[site];
}

export function isHttpsLink(link: URL) {
    return link.protocol === 'https:';
}

export function isValidUrl(link: string | undefined): boolean {
    try {
        const deeplink = new URL(link ?? '');
        return !!deeplink && isHttpsLink(deeplink);
    } catch (e) {
        return false;
    }
}

export const currentSite = getCurrentSite(window.location.hostname);
export const currentProductionDomain = siteDomainMap[currentSite];
